import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCatalog } from "../api/catalog";

const initialState = {
  store: null,
  products: [],
  pagination: null,
};

function appendUniqueProducts(current, incoming) {
  const productsById = new Map(
    current.map((product) => [product.id, product])
  );
  incoming.forEach((product) => productsById.set(product.id, product));
  return Array.from(productsById.values());
}

export function useInfiniteCatalog(storeSlug) {
  const [catalog, setCatalog] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const generationRef = useRef(0);
  const loadMoreControllerRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const controller = new AbortController();
    loadMoreControllerRef.current?.abort();

    setCatalog(initialState);
    setLoading(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setError(null);

    fetchCatalog(storeSlug, {
      page: 1,
      limit: 12,
      signal: controller.signal,
    })
      .then((result) => {
        if (generationRef.current !== generation) return;
        setCatalog(result);
        if (result?.store?.name) {
          document.title = `${result.store.name} | AtomicPay`;
        }
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError);
      })
      .finally(() => {
        if (generationRef.current === generation) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadToken, storeSlug]);

  const hasMore = Boolean(
    catalog.pagination &&
      catalog.pagination.page < catalog.pagination.totalPages
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || loadingMoreRef.current) return;
    const generation = generationRef.current;
    const nextPage = Number(catalog.pagination?.page || 1) + 1;
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const result = await fetchCatalog(storeSlug, {
        page: nextPage,
        limit: catalog.pagination?.limit || 12,
        signal: controller.signal,
      });
      if (generationRef.current !== generation) return;
      setCatalog((current) => ({
        store: result.store || current.store,
        products: appendUniqueProducts(
          current.products,
          result.products || []
        ),
        pagination: result.pagination,
      }));
    } catch (requestError) {
      if (requestError.name !== "AbortError") setError(requestError);
    } finally {
      if (generationRef.current === generation) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [
    catalog.pagination,
    hasMore,
    loading,
    storeSlug,
  ]);

  useEffect(
    () => () => {
      loadMoreControllerRef.current?.abort();
    },
    []
  );

  return {
    ...catalog,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    retry: () => setReloadToken((current) => current + 1),
  };
}
